import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { Settings, Loader2, Save, Shield, Lock, Eye, CheckCircle, XCircle, Key } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [taxPercentage, setTaxPercentage] = useState(0);
  const [message, setMessage] = useState('');
  
  // 2FA states
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [qrCodeData, setQrCodeData] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading2FA, setLoading2FA] = useState(false);
  const [message2FA, setMessage2FA] = useState('');

  useEffect(() => {
    const isAuth = localStorage.getItem('adminAuth');
    if (!isAuth) {
      navigate('/admin');
      return;
    }
    fetchSettings();
  }, [navigate]);

  const fetchSettings = async () => {
    try {
      const settingsRes = await axios.get(`${API}/admin/settings`);
      setTaxPercentage(settingsRes.data.tax_percentage || 0);
      // Default 2FA to false - user can check status when enabling
      setTwoFAEnabled(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      await axios.put(`${API}/admin/settings?tax_percentage=${taxPercentage}`);
      setMessage('Settings saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSetup2FA = async () => {
    setLoading2FA(true);
    setMessage2FA('');
    
    try {
      const response = await axios.post(`${API}/admin/2fa/setup`);
      setQrCodeData(response.data.qr_code);
      setBackupCodes(response.data.backup_codes);
      setShowQRCode(true);
      setMessage2FA('Scan the QR code with Google Authenticator app');
    } catch (error) {
      setMessage2FA('Failed to setup 2FA. Please try again.');
    } finally {
      setLoading2FA(false);
    }
  };

  const handleEnable2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setMessage2FA('Please enter a valid 6-digit code');
      return;
    }

    setLoading2FA(true);
    setMessage2FA('');

    try {
      await axios.post(`${API}/admin/2fa/enable`, { code: verificationCode });
      setTwoFAEnabled(true);
      setShowQRCode(false);
      setVerificationCode('');
      setMessage2FA('✅ 2FA enabled successfully!');
      setTimeout(() => setMessage2FA(''), 3000);
    } catch (error) {
      setMessage2FA('Invalid code. Please try again.');
    } finally {
      setLoading2FA(false);
    }
  };

  const handleDisable2FA = async () => {
    const password = prompt('Enter your password to disable 2FA:');
    if (!password) return;

    setLoading2FA(true);
    setMessage2FA('');

    try {
      await axios.post(`${API}/admin/2fa/disable`, { password });
      setTwoFAEnabled(false);
      setMessage2FA('2FA disabled successfully');
      setTimeout(() => setMessage2FA(''), 3000);
    } catch (error) {
      setMessage2FA('Failed to disable 2FA. Check your password.');
    } finally {
      setLoading2FA(false);
    }
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center mb-8">
          <Settings className="w-8 h-8 text-[#1E88E5] mr-3" />
          <h1 className="text-3xl font-bold text-black">Settings</h1>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-12 h-12 text-[#1E88E5] animate-spin" />
          </div>
        ) : (
          <div className="space-y-8">
            {/* Affiliate Commission Settings */}
            <div className="bg-white rounded-lg shadow-md p-8 max-w-2xl">
              <h2 className="text-xl font-bold text-black mb-6">Affiliate Commission Settings</h2>
              
              <form onSubmit={handleSave} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-black mb-2">
                    Tax Percentage *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={taxPercentage}
                      onChange={(e) => setTaxPercentage(parseFloat(e.target.value) || 0)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] focus:border-transparent"
                      placeholder="0"
                      required
                    />
                    <span className="absolute right-4 top-3.5 text-gray-500">%</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    Tax percentage will be deducted from revenue before calculating affiliate commissions
                  </p>
                </div>

                <div className="bg-blue-50 border-l-4 border-[#1E88E5] p-4 rounded">
                  <h3 className="text-sm font-semibold text-black mb-2">Commission Formula:</h3>
                  <p className="text-sm text-gray-700">
                    <strong>Total Commission</strong> = (Revenue - (Revenue × Tax %)) × Commission %
                  </p>
                </div>

                {message && (
                  <div className={`p-4 rounded-lg ${
                    message.includes('success') 
                      ? 'bg-green-50 text-green-700 border border-green-200' 
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}>
                    {message}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={saving}
                  className="w-full px-6 py-3 bg-[#1E88E5] text-white font-semibold rounded-lg hover:bg-[#1565C0] transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-5 h-5" />
                      <span>Save Settings</span>
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Security Settings - 2FA */}
            <div className="bg-white rounded-lg shadow-md p-8 max-w-2xl">
              <div className="flex items-center mb-6">
                <Shield className="w-6 h-6 text-[#1E88E5] mr-3" />
                <h2 className="text-xl font-bold text-black">Security Settings</h2>
              </div>

              {/* 2FA Status */}
              <div className="mb-6 p-4 rounded-lg border-2 flex items-center justify-between" style={{
                borderColor: twoFAEnabled ? '#4CAF50' : '#FF9800',
                backgroundColor: twoFAEnabled ? '#E8F5E9' : '#FFF3E0'
              }}>
                <div className="flex items-center">
                  {twoFAEnabled ? (
                    <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
                  ) : (
                    <XCircle className="w-6 h-6 text-orange-600 mr-3" />
                  )}
                  <div>
                    <p className="font-semibold text-black">Two-Factor Authentication</p>
                    <p className="text-sm text-gray-600">
                      {twoFAEnabled ? 'Enabled - Your account is protected' : 'Disabled - Recommended to enable'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  {twoFAEnabled ? (
                    <button
                      onClick={handleDisable2FA}
                      disabled={loading2FA}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm"
                    >
                      Disable
                    </button>
                  ) : (
                    <button
                      onClick={handleSetup2FA}
                      disabled={loading2FA}
                      className="px-4 py-2 bg-[#1E88E5] text-white rounded-lg hover:bg-[#1565C0] disabled:opacity-50 text-sm flex items-center space-x-2"
                    >
                      <Key className="w-4 h-4" />
                      <span>Enable 2FA</span>
                    </button>
                  )}
                </div>
              </div>

              {/* QR Code Setup */}
              {showQRCode && (
                <div className="border-2 border-[#1E88E5] rounded-lg p-6 space-y-4">
                  <h3 className="text-lg font-semibold text-black">Setup Two-Factor Authentication</h3>
                  
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-900 mb-2">
                      <strong>Step 1:</strong> Install Google Authenticator or Authy on your phone
                    </p>
                    <p className="text-sm text-blue-900">
                      <strong>Step 2:</strong> Scan this QR code with the app
                    </p>
                  </div>

                  <div className="flex justify-center my-4">
                    <img src={qrCodeData} alt="2FA QR Code" className="border-4 border-gray-200 rounded-lg" />
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                    <p className="text-sm font-semibold text-yellow-900 mb-2">🔑 Backup Codes (Save These!)</p>
                    <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                      {backupCodes.map((code, idx) => (
                        <div key={idx} className="bg-white p-2 rounded border">{code}</div>
                      ))}
                    </div>
                    <p className="text-xs text-yellow-800 mt-2">
                      Save these codes in a safe place. You can use them if you lose access to your authenticator app.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-black mb-2">
                      Enter 6-digit code from your app
                    </label>
                    <input
                      type="text"
                      maxLength="6"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1E88E5] text-center text-2xl font-mono"
                      placeholder="000000"
                    />
                  </div>

                  <button
                    onClick={handleEnable2FA}
                    disabled={loading2FA || verificationCode.length !== 6}
                    className="w-full px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center space-x-2"
                  >
                    {loading2FA ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Verifying...</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        <span>Enable 2FA</span>
                      </>
                    )}
                  </button>
                </div>
              )}

              {message2FA && (
                <div className={`mt-4 p-4 rounded-lg ${
                  message2FA.includes('✅') || message2FA.includes('success')
                    ? 'bg-green-50 text-green-700 border border-green-200' 
                    : 'bg-orange-50 text-orange-700 border border-orange-200'
                }`}>
                  {message2FA}
                </div>
              )}

              {/* Security Info */}
              <div className="mt-6 bg-blue-50 border-l-4 border-[#1E88E5] p-4 rounded">
                <h3 className="text-sm font-semibold text-black mb-2">🔒 Security Features Active:</h3>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>✅ Password hashing with bcrypt</li>
                  <li>✅ JWT session management (24-hour expiry)</li>
                  <li>✅ Failed login auto-ban (5 attempts)</li>
                  <li>✅ Security headers (CSP, HSTS)</li>
                  <li>✅ Admin activity logging</li>
                  <li>{twoFAEnabled ? '✅' : '⏸️'} Two-Factor Authentication (2FA)</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
